import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Platform, Pressable, Text, View } from "react-native"
import type { ViewStyle } from "react-native"
import { Gesture, GestureDetector } from "react-native-gesture-handler"
import { runOnJS } from "react-native-reanimated"
import RNSvg, { Circle, Defs, LinearGradient, Line, Path, Stop } from "react-native-svg"
import { numberFormat } from "@ecountabl/lib"
import { textStyles } from "../style"
import { ThemeContext } from "../utility/context/ThemeContext"
import ValueLabelCard from "../utility/value-label-card"
import LightweightPieChart from "../components/impact/lightweight-pie-chart"
import { EnvironmentalImpactChartTooltip } from "../components/impact/environmental-impact-chart-tooltip"

const ROW_GAP = 20
const SECTION_MAX_WIDTH = 1500
const SOCIAL_ROW_MIN = 500
const MIN_ENV_INLINE = 500
export const COMMUNITY_EMBED_MIN_ROW_OUTER_WIDTH =
  MIN_ENV_INLINE + ROW_GAP + SOCIAL_ROW_MIN

const ENV_CHART_PADDING = { left: 12, right: 12, top: 12, bottom: 18 } as const

type SolarPoint = {
  CumulativeSum: number
  Label: string
  MonthNum: string
  YearNum: string
}

type SocialRow = {
  Color: string
  Title: string
  Value: number
}

export type CommunityImpactEmbedPayload = {
  Solar: SolarPoint[]
  EquivalentsWithIcons: Record<string, { value: number; icon: string }>
  Equivalents?: unknown
  Social_Impact: SocialRow[]
}

type SolarChartProps = {
  data: SolarPoint[]
}

const CommunityEmbedSolarChart = ({ data }: SolarChartProps): JSX.Element => {
  const theme = useContext(ThemeContext).theme
  const { primary, textMain, bgMain, borderColor } = theme.colors

  const [width, setWidth] = useState(0)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const containerRef = useRef<React.ElementRef<typeof View> | null>(null)

  const points = useMemo(() => {
    const sorted = (data || []).slice().sort((a, b) => {
      const ay = Number(a?.YearNum || 0)
      const by = Number(b?.YearNum || 0)
      if (ay !== by) return ay - by
      const am = Number(a?.MonthNum || 0)
      const bm = Number(b?.MonthNum || 0)
      return am - bm
    })
    return sorted.map((p) => ({
      label: String(p?.Label || ""),
      value: Math.max(0, Number(p?.CumulativeSum || 0)),
    }))
  }, [data])

  useEffect(() => {
    if (points.length === 0) {
      if (focusedIndex !== -1) setFocusedIndex(-1)
      return
    }
    if (focusedIndex < 0 || focusedIndex >= points.length) {
      setFocusedIndex(points.length - 1)
    }
  }, [focusedIndex, points.length])

  const chartHeight = 180
  const padding = ENV_CHART_PADDING
  const plotW = Math.max(1, width - padding.left - padding.right)
  const plotH = Math.max(1, chartHeight - padding.top - padding.bottom)

  const maxY = useMemo(() => {
    const m = Math.max(1, ...points.map((p) => p.value))
    return m * 1.05
  }, [points])

  const coords = useMemo(() => {
    if (points.length === 0 || width <= 0) return []
    const n = points.length
    return points.map((p, i) => {
      const x = n <= 1 ? padding.left : (padding.left + ((i / (n - 1)) * plotW))
      const y = padding.top + plotH - (((p.value || 0) / maxY) * plotH)
      return { x, y }
    })
  }, [maxY, padding.left, padding.top, plotH, plotW, points, width])

  const { linePath, areaPath } = useMemo(() => {
    if (coords.length === 0) return { linePath: "", areaPath: "" }
    const line = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`).join(" ")
    const baseY = padding.top + plotH
    const area = `${line} L ${coords[coords.length - 1].x} ${baseY} L ${coords[0].x} ${baseY} Z`
    return { linePath: line, areaPath: area }
  }, [coords, padding.top, plotH])

  const focus = useMemo(() => {
    if (focusedIndex < 0 || focusedIndex >= points.length) return null
    const c = coords[focusedIndex]
    if (!c) return null
    return { x: c.x, y: c.y, point: points[focusedIndex] }
  }, [coords, focusedIndex, points])

  const toRgba = (color: string, alpha: number): string => {
    const c = String(color || "").trim()
    if (!c) return `rgba(37,99,235,${alpha})`
    if (c.startsWith("rgba(")) {
      const rgbaMatch = c.match(/^rgba\(\s*([0-9]+\s*,\s*[0-9]+\s*,\s*[0-9]+)(?:\s*,\s*[\d.]+)?\s*\)$/)
      if (rgbaMatch?.[1]) return `rgba(${rgbaMatch[1]},${alpha})`
      return `rgba(37,99,235,${alpha})`
    }
    if (c.startsWith("rgb(")) {
      const inner = c.replace("rgb(", "").replace(")", "")
      return `rgba(${inner},${alpha})`
    }
    let hex = c.startsWith("#") ? c.slice(1) : c
    if (hex.length === 3) hex = hex.split("").map((ch) => ch + ch).join("")
    if (hex.length !== 6) return `rgba(37,99,235,${alpha})`
    const n = parseInt(hex, 16)
    const r = (n >> 16) & 255
    const g = (n >> 8) & 255
    const b = n & 255
    return `rgba(${r},${g},${b},${alpha})`
  }

  const handleFocusAtX = useCallback((x: number) => {
    if (points.length === 0) return
    const clampedX = Math.max(padding.left, Math.min(width - padding.right, x))
    const t = plotW <= 1 ? 0 : (clampedX - padding.left) / plotW
    const idx = Math.round(t * (points.length - 1))
    setFocusedIndex(Math.max(0, Math.min(points.length - 1, idx)))
  }, [padding.left, padding.right, plotW, points.length, width])

  const handleFocusFromClientX = (clientX: number) => {
    if (Platform.OS !== "web") return
    const node = containerRef.current as unknown as
      | { getBoundingClientRect?: () => { left: number } }
      | null
    const rect = node?.getBoundingClientRect?.()
    if (!rect) return
    handleFocusAtX(clientX - rect.left)
  }

  const panGesture = useMemo(() => {
    if (Platform.OS === "web") return null
    return Gesture.Pan()
      .minDistance(0)
      .activeOffsetX([-1, 1])
      .activeOffsetY([-1, 1])
      .onBegin((e) => {
        runOnJS(handleFocusAtX)(e.x)
      })
      .onUpdate((e) => {
        runOnJS(handleFocusAtX)(e.x)
      })
  }, [handleFocusAtX])

  const webHandlers = Platform.OS === "web"
    ? {
        onMouseMove: (e: unknown): void => {
          const ne = (e as { nativeEvent?: { clientX?: number } })?.nativeEvent
          const clientX = ne?.clientX
          if (typeof clientX === "number") handleFocusFromClientX(clientX)
        },
        onPointerDown: (e: unknown): void => {
          const ne =
            (e as {
              nativeEvent?: { clientX?: number; pointerId?: number; target?: unknown }
            })?.nativeEvent
          if (typeof ne?.clientX === "number") handleFocusFromClientX(ne.clientX)
          const t = ne?.target as { setPointerCapture?: (id: number) => void } | undefined
          if (typeof ne?.pointerId === "number") t?.setPointerCapture?.(ne.pointerId)
        },
        onPointerMove: (e: unknown): void => {
          const ne = (e as { nativeEvent?: { clientX?: number } })?.nativeEvent
          const clientX = ne?.clientX
          if (typeof clientX === "number") handleFocusFromClientX(clientX)
        },
        onTouchStart: (e: unknown): void => {
          const ne = (
            e as { nativeEvent?: { touches?: Array<{ clientX?: number }> } }
          )?.nativeEvent
          const clientX = ne?.touches?.[0]?.clientX
          if (typeof clientX === "number") handleFocusFromClientX(clientX)
          ;(e as { preventDefault?: () => void })?.preventDefault?.()
        },
        onTouchMove: (e: unknown): void => {
          const ne = (
            e as { nativeEvent?: { touches?: Array<{ clientX?: number }> } }
          )?.nativeEvent
          const clientX = ne?.touches?.[0]?.clientX
          if (typeof clientX === "number") handleFocusFromClientX(clientX)
          ;(e as { preventDefault?: () => void })?.preventDefault?.()
        },
      }
    : {}

  const chartPlot = (
    <View
      style={[
        { position: "relative", width: "100%", height: chartHeight, marginTop: 6 },
        Platform.OS === "web"
          ? ({
              touchAction: "none",
              userSelect: "none",
              cursor: "crosshair",
            } as ViewStyle & {
              touchAction?: string
              userSelect?: string
              cursor?: string
            })
          : undefined,
      ]}
      pointerEvents="box-only"
      ref={containerRef}
      onLayout={(e) => {
        const w = e.nativeEvent.layout.width
        if (w && w !== width) setWidth(w)
      }}
      onStartShouldSetResponderCapture={() => true}
      onMoveShouldSetResponderCapture={() => true}
      onStartShouldSetResponder={() => true}
      onMoveShouldSetResponder={() => true}
      onResponderTerminationRequest={() => false}
      onResponderGrant={(e) => {
        handleFocusAtX(e.nativeEvent.locationX)
      }}
      onResponderMove={(e) => {
        handleFocusAtX(e.nativeEvent.locationX)
      }}
      {...webHandlers}
    >
      <RNSvg width={width || 1} height={chartHeight}>
        <Defs>
          <LinearGradient
            id="communityEmbedEnvArea"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1={padding.top}
            x2="0"
            y2={padding.top + plotH}
          >
            <Stop offset="0" stopColor={primary} stopOpacity={0.28} />
            <Stop offset="1" stopColor={primary} stopOpacity={0} />
          </LinearGradient>
        </Defs>

        <Line
          x1={padding.left}
          x2={(width || 1) - padding.right}
          y1={padding.top + plotH}
          y2={padding.top + plotH}
          stroke={toRgba(borderColor, 0.6)}
          strokeWidth={1}
        />

        {areaPath ? <Path d={areaPath} fill="url(#communityEmbedEnvArea)" /> : null}
        {linePath ? <Path d={linePath} fill="none" stroke={primary} strokeWidth={2} /> : null}

        {focus ? (
          <>
            <Line
              x1={focus.x}
              x2={focus.x}
              y1={padding.top}
              y2={padding.top + plotH}
              stroke={toRgba(primary, 0.5)}
              strokeWidth={1}
            />
            <Circle cx={focus.x} cy={focus.y} r={4} fill={primary} />
            <Circle cx={focus.x} cy={focus.y} r={2} fill={bgMain} />
          </>
        ) : null}
      </RNSvg>

      {focus ? (
        <EnvironmentalImpactChartTooltip
          label={focus.point.label}
          valueText={`${numberFormat(Math.round(focus.point.value), { shortenLargeNumbers: true })} kW`}
          focusX={focus.x}
          focusY={focus.y}
          chartWidth={width || 1}
          chartHeight={chartHeight}
          bgMain={bgMain}
          borderColor={borderColor}
          textMain={textMain}
        />
      ) : null}
    </View>
  )

  return (
    <View style={{ paddingHorizontal: 10, overflow: "hidden", marginBottom: 12, gap: 10 }}>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text style={[textStyles.h4, { color: primary }]}>
          kW of Solar Financed Since Inception
        </Text>
      </View>
      {panGesture ? <GestureDetector gesture={panGesture}>{chartPlot}</GestureDetector> : chartPlot}
      <Text style={[textStyles.subscript, { color: theme.colors.textSecond, marginTop: 6 }]}>
        {Platform.OS === "web" ? "Hover" : "Drag"} to explore history
      </Text>
    </View>
  )
}

type EnvironmentalColumnProps = {
  data: CommunityImpactEmbedPayload
}

const CommunityEmbedEnvironmentalColumn = ({
  data,
}: EnvironmentalColumnProps): JSX.Element => {
  const theme = useContext(ThemeContext)
  const { second, textMain } = theme.theme.colors
  const equivalents = Object.keys(data.EquivalentsWithIcons || {})
  const [columnWidth, setColumnWidth] = useState(0)
  const isSingleColumnEquivalencies = columnWidth > 0 && columnWidth <= 430

  return (
    <View
      style={{ flexDirection: "column", width: "100%", gap: 10 }}
      onLayout={(e) => {
        const w = Math.round(e.nativeEvent.layout.width)
        if (w !== columnWidth) setColumnWidth(w)
      }}
    >
      <Text style={[textStyles.h3, { color: textMain }]}>
        Environmental Impact
      </Text>
      <CommunityEmbedSolarChart data={data.Solar || []} />
      {equivalents?.length > 0 ? (
        <View style={{ paddingHorizontal: 6, width: "100%" }}>
          <Text style={[textStyles.body, { color: second, marginBottom: 6 }]}>
            This is equivalent to...
          </Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: isSingleColumnEquivalencies ? "center" : "space-evenly",
              alignItems: "stretch",
              marginVertical: 4,
              gap: 12,
            }}
          >
            {equivalents.map((eq) => {
              const [iconFamily, iconName] = data.EquivalentsWithIcons[eq].icon.split("::")
              const value = numberFormat(
                data.EquivalentsWithIcons[eq].value,
                { shortenLargeNumbers: true }
              )
              return (
                <ValueLabelCard
                  key={eq}
                  label={eq}
                  value={value}
                  icon={{ family: iconFamily, name: iconName }}
                  containerStyle={{
                    minWidth: isSingleColumnEquivalencies ? 0 : 250,
                    maxWidth: 300,
                    flexBasis: isSingleColumnEquivalencies ? "100%" : 250,
                    flexGrow: isSingleColumnEquivalencies ? 0 : 1,
                    width: isSingleColumnEquivalencies ? "100%" : undefined,
                  }}
                />
              )
            })}
          </View>
        </View>
      ) : null}
    </View>
  )
}

const socialHex = (raw: string | undefined): string => {
  let h = String(raw ?? "").replace("#", "").trim()
  if (h.length === 3) {
    h = h.split("").map((c) => c + c).join("")
  }
  if (/^[0-9a-fA-F]{6}$/.test(h)) {
    return h
  }
  return "2563eb"
}

type SocialBlockProps = {
  chartData: SocialRow[]
  /** Page-level: environmental + social side by side */
  pageSplit: boolean
}

const CommunityEmbedSocialBlock = ({
  chartData,
  pageSplit,
}: SocialBlockProps): JSX.Element => {
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [pieSlotW, setPieSlotW] = useState(0)
  const [blockWidth, setBlockWidth] = useState(0)
  const theme = useContext(ThemeContext).theme
  const { textMain, textAlt, primary } = theme.colors
  const tableBorder = `${textAlt}55`

  const rows = useMemo(
    () => (chartData || []).filter((r) => Number(r?.Value) > 0),
    [chartData]
  )

  const pieData = useMemo(
    () =>
      rows.map((row, index) => {
        const six = socialHex(row.Color)
        const dim = focusedIndex !== -1 && focusedIndex !== index
        return {
          value: Number(row.Value),
          color: dim ? `#${six}40` : `#${six}`,
          key: `emb-${index}-${row.Title}`,
        }
      }),
    [rows, focusedIndex]
  )

  const pieSize =
    pieSlotW > 0 ? Math.max(120, Math.min(Math.floor(pieSlotW), 280)) : 200

  const moneyFmt = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }),
    []
  )

  const rowHighlight =
    theme.name === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"

  const unfocus = () => setFocusedIndex(-1)

  if (rows.length === 0) {
    return (
      <View style={{ width: "100%" }}>
        <Text style={[textStyles.body, { color: textAlt }]}>
          No social impact data available yet.
        </Text>
      </View>
    )
  }

  const pieLegendHorizontal =
    pageSplit || blockWidth > SOCIAL_ROW_MIN

  const pieColumnStyle = pageSplit
    ? ({
        width: 280,
        flexShrink: 0,
        justifyContent: "center",
        alignSelf: "stretch",
      } as const)
    : pieLegendHorizontal
      ? ({ flex: 1, minWidth: 0, justifyContent: "center" } as const)
      : ({ width: "100%" } as const)

  const legendColumnStyle = pageSplit
    ? ({
        flexShrink: 1,
        minWidth: 200,
        maxWidth: 420,
        alignSelf: "stretch",
      } as const)
    : pieLegendHorizontal
      ? ({ flex: 1, minWidth: 0 } as const)
      : ({ width: "100%" } as const)

  const rootStyle = pageSplit
    ? ({
        alignSelf: "flex-start",
        minWidth: SOCIAL_ROW_MIN + 1,
      } as const)
    : ({ width: "100%" } as const)

  const innerStackStyle = pageSplit
    ? ({
        alignSelf: "flex-start",
        width: "auto" as const,
        flexDirection: "column" as const,
        gap: 8,
        justifyContent: "space-between",
      } as const)
    : ({
        width: "100%",
        flexDirection: "column" as const,
        gap: 8,
      } as const)

  const pieRowStyle = pageSplit
    ? ({
        flexDirection: "row" as const,
        gap: 12,
        alignItems: "stretch" as const,
        alignSelf: "flex-start" as const,
        width: "auto" as const,
      } as const)
    : ({
        width: "100%",
        flexDirection: pieLegendHorizontal ? "row" : "column",
        gap: pieLegendHorizontal ? 12 : 8,
        alignItems: "stretch",
      } as const)

  return (
    <View
      style={rootStyle}
      onLayout={(e) => {
        const w = Math.round(e.nativeEvent.layout.width)
        if (w !== blockWidth) setBlockWidth(w)
      }}
    >
      <View style={innerStackStyle}>
        <Text style={[textStyles.h4, { color: primary, alignSelf: "flex-start" }]}>
          Amount in Mission-Aligned Loans Financed Since Inception
        </Text>
        <View style={pieRowStyle}>
          <View style={pieColumnStyle}>
            <View
              style={{
                width: "100%",
                minHeight: 140,
                alignItems: "center",
                justifyContent: "center",
              }}
              onLayout={(e) => {
                const w = e.nativeEvent.layout.width
                if (w && Math.round(w) !== pieSlotW) {
                  setPieSlotW(Math.round(w) * 0.66)
                }
              }}
            >
              <LightweightPieChart
                data={pieData}
                size={pieSize}
                innerRadius={0}
                onSliceHover={(index) => setFocusedIndex(index)}
                onSliceLeave={unfocus}
              />
            </View>
          </View>

          <View
            style={[
              legendColumnStyle,
              {
                borderWidth: 1,
                borderColor: tableBorder,
                borderRadius: 10,
                overflow: "hidden",
              },
            ]}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-end",
                borderBottomWidth: 1,
                borderBottomColor: tableBorder,
                paddingVertical: 5,
                paddingHorizontal: 4,
              }}
            >
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: "700",
                  color: textAlt,
                  paddingLeft: 4,
                  paddingRight: 8,
                }}
              >
                Impact Area
              </Text>
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: textAlt,
                  paddingRight: 4,
                  textAlign: "right",
                  fontVariant: ["tabular-nums"],
                }}
              >
                $
              </Text>
            </View>
            {rows.map((row, i) => {
              const six = socialHex(row.Color)
              const amount = moneyFmt.format(Math.round(Number(row.Value || 0)))
              const active = focusedIndex === i
              const faded = focusedIndex !== -1 && !active
              return (
                <Pressable
                  key={pieData[i]?.key ?? `emb-row-${i}`}
                  onHoverIn={() => setFocusedIndex(i)}
                  onHoverOut={unfocus}
                  onPressIn={() => setFocusedIndex(i)}
                  onPressOut={unfocus}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    borderBottomWidth: i < rows.length - 1 ? 1 : 0,
                    borderBottomColor: tableBorder,
                    backgroundColor: active ? rowHighlight : "transparent",
                    opacity: faded ? 0.4 : 1,
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      minWidth: 0,
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                      paddingVertical: 4,
                      paddingLeft: 4,
                      paddingRight: 8,
                    }}
                  >
                    <View
                      style={{
                        backgroundColor: `#${six}`,
                        width: 10,
                        height: 10,
                        borderRadius: 5,
                        flexShrink: 0,
                      }}
                    />
                    <Text
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 12,
                        lineHeight: 16,
                        fontWeight: "700",
                        color: textMain,
                        fontFamily: "poppins-semi-bold",
                      }}
                    >
                      {row.Title}
                    </Text>
                  </View>
                  <Text
                    style={{
                      fontSize: 12,
                      lineHeight: 16,
                      fontWeight: "700",
                      color: textMain,
                      fontFamily: "poppins-semi-bold",
                      paddingVertical: 4,
                      paddingRight: 4,
                      paddingLeft: 0,
                      fontVariant: ["tabular-nums"],
                    }}
                  >
                    {amount}
                  </Text>
                </Pressable>
              )
            })}
          </View>
        </View>
      </View>
    </View>
  )
}

type EmbedBodyProps = {
  data: CommunityImpactEmbedPayload
  useRowLayout: boolean
}

export const CommunityImpactEmbedBody = ({
  data,
  useRowLayout,
}: EmbedBodyProps): JSX.Element => {
  const theme = useContext(ThemeContext)
  const { textMain } = theme.theme.colors

  return (
    <View
      style={{
        width: "100%",
        gap: ROW_GAP,
        flexDirection: useRowLayout ? "row" : "column",
        flexWrap: "nowrap",
        justifyContent: useRowLayout ? "space-evenly" : "flex-start",
        alignItems: useRowLayout ? "flex-start" : "stretch",
      }}
    >
      <View
        style={[
          { flexDirection: "column", minWidth: 300 },
          useRowLayout
            ? { flex: 1, minWidth: 0, maxWidth: SECTION_MAX_WIDTH }
            : { width: "100%" },
        ]}
      >
        <CommunityEmbedEnvironmentalColumn data={data} />
      </View>

      <View
        style={[
          { flexDirection: "column", gap: 10 },
          useRowLayout
            ? {
                flexGrow: 0,
                flexShrink: 0,
                alignSelf: "flex-start",
                maxWidth: SECTION_MAX_WIDTH,
              }
            : { width: "100%", minWidth: 0 },
        ]}
      >
        <Text style={[textStyles.h3, { color: textMain }]}>Social Impact</Text>
        <CommunityEmbedSocialBlock
          chartData={data.Social_Impact || []}
          pageSplit={useRowLayout}
        />
      </View>
    </View>
  )
}
